// =============================================================================
// ciblesLiens.js — ce que retrouve l'écran visé par un lien de notification
// (centre de notifications, relecture du 30/09/2026).
// -----------------------------------------------------------------------------
// Un lien n'est un lien que si l'écran qu'il ouvre LIT son paramètre et montre
// l'objet à la personne qui le suit. Deux liens menaient ailleurs :
//   • « Résultats publiés : CA du 24 septembre », adressée aux participants de
//     la séance archivée, ouvrait « Accès réservé » : la vue Résultats ne
//     s'ouvrait qu'à l'administrateur et au votant émargé de la séance EN COURS ;
//   • « Fiche de Jean Durand (SMH) » (journal des administrateurs) ouvrait
//     l'annuaire complet : il ne lisait que { mutuelle }, jamais { delegateId }.
// Règles pures, testées par src/notificationsLiens.test.js ; le petit poids
// est celui du bundle principal (App.jsx rend les deux écrans).
// =============================================================================
import { participantDansSeance } from "./instancesNav";

/**
 * Ce que la vue « Résultats » montre à la personne connectée.
 *   • administrateur, ou votant émargé de la séance en cours : le direct et
 *     toutes les séances archivées publiées (comme avant) ;
 *   • membre connecté à Aramis : les séances archivées PUBLIÉES dont il était
 *     participant (fiche, e-mail ou alias, le rapprochement de la
 *     convocation), c'est-à-dire exactement le public de la notification ;
 *     jamais le direct d'une séance où il n'a pas émargé ;
 *   • sinon rien : « Accès réservé ».
 * @param {{ history?: object[], moi?: object|null, admin?: boolean, votantConnecte?: boolean }} p
 * @returns {{ acces: boolean, direct: boolean, archives: object[] }}
 */
export function lectureResultats({ history = [], moi = null, admin = false, votantConnecte = false } = {}) {
  const publiees = (history || []).filter((s) => s && s.resultsPublished);
  if (admin || votantConnecte) return { acces: true, direct: true, archives: publiees };
  if (!moi || !moi.id) return { acces: false, direct: false, archives: [] };
  return { acces: true, direct: false, archives: publiees.filter((s) => participantDansSeance(s.participants, moi)) };
}

/**
 * Le haut de la vue « Résultats » et ce qu'elle déroule (grammaire du
 * 27/10/2026) : titre du bandeau ; « En direct » et « Archivées » en
 * sélecteur segmenté (barre de la liste, src/BarreResultats.js) seulement
 * quand les deux existent ; sans direct, les archives seules.
 * @param {{ lecture?: { direct?: boolean, archives?: object[] }, vue?: "direct"|"archives", nbOuverts?: number }} p
 * @returns {{ titre: string, segments: boolean, options: object[], montrerDirect: boolean, montrerArchives: boolean }}
 */
export function affichageResultats({ lecture = null, vue = "direct", nbOuverts = 0 } = {}) {
  const direct = !!(lecture && lecture.direct);
  const nbArchives = ((lecture && lecture.archives) || []).length;
  const segments = direct && nbArchives > 0;
  return {
    titre: direct ? "Résultats en direct" : "Résultats publiés",
    segments,
    options: segments ? [
      { valeur: "direct", libelle: "En direct", compte: nbOuverts },
      { valeur: "archives", libelle: "Archivées", compte: nbArchives },
    ] : [],
    montrerDirect: direct && (!segments || vue !== "archives"),
    montrerArchives: !direct || (segments && vue === "archives"),
  };
}

/**
 * La fiche que l'annuaire doit montrer pour navTo("athos-annuaire", { delegateId }) :
 * { id, mutuelle } d'une fiche active, ou null (inconnue ou désactivée :
 * l'annuaire ne la liste pas).
 */
export function ficheAnnuaireVisee(delegates, delegateId) {
  if (!delegateId) return null;
  const d = (delegates || []).find((x) => x && String(x.id) === String(delegateId));
  if (!d || d.actif === false) return null;
  return { id: d.id, mutuelle: d.mutuelle || "" };
}

/**
 * Ce qu'ouvre navTo("athos-reunions", { reunionId, section }) :
 *   • "carte" : la carte de la réunion, dépliée ; son compte rendu Noota y vit,
 *     « accessible à tous les invités ». Pour un membre toujours, et pour tous
 *     quand le lien nomme une section (« Compte rendu disponible ») ;
 *   • "editeur" : la fenêtre d'invitation (enregistrer, envoyer une mise à
 *     jour à tous les invités), outil des administrateurs : seulement pour un
 *     administrateur, et sans section demandée (boutons « Ouvrir » des séances) ;
 *   • null : réunion inconnue, ou qu'un membre ne voit pas.
 * Avant le 30/09, le lien ouvrait l'éditeur à tout le monde : un membre suivant
 * « Compte rendu disponible » arrivait dans l'outil d'envoi, sans le compte rendu.
 * @param {{ reunion?: object|null, ancienne?: object|null, admin?: boolean, section?: string|null, visible?: boolean }} p
 *   `reunion` : réunion Aramis ; `ancienne` : réunion de l'ancien agenda (sans carte dépliable).
 * @returns {"carte"|"editeur"|null}
 */
export function ouvertureReunion({ reunion = null, ancienne = null, admin = false, section = null, visible = true } = {}) {
  if (reunion) {
    if (admin && !section) return "editeur";
    return admin || visible ? "carte" : null;
  }
  return ancienne && admin && !section ? "editeur" : null;
}
